// /full-color-3d-printing-service/ (PrintingProcessPage.js). Code-owned since
// 2026-11-04; gatsby-node skips the Contentful entry.
// GSC Aug–Oct 2026: 264 clicks. Top queries kept in the title, H1 and H2s:
// "full color 3d printing service", "full color 3d print service",
// "color 3d printing service", "multi color / multicolor 3d printing service",
// "full color 3d printing".
// truth.md lists no in-house full-color printer (the capacity row is an FDM
// farm, SLA resin, large-format and stainless steel), and /services/3d-printing/
// says full color comes from painting. So this page sells the painted route
// we do in-house, and takes printed-in-color jobs as quoted per project
// (traffic-first demand test). The old page's printer model and its
// "same printer as a film studio" line are left out until the owner confirms
// them. The "no sandstone" answer is kept from the old page.
const {
  pricing,
  photoRoot,
  reviewedDate,
  studios,
  delivery,
  noFile,
  hobby,
  quoteSteps,
  handoffStep,
} = require('./shared')
const printing = require('../printing-service')

const page = {
  path: '/full-color-3d-printing-service/',
  crumb: 'Full-color 3D printing',
  title: 'Full Color 3D Printing Service | Multicolor 3D Prints',
  description:
    'Full color 3D printing service for figurines, mascots, architectural models and replicas: hand-painted resin or printed-in-color parts, quoted per project.',
  heading: 'Full Color 3D Printing Service',
  eyebrow: 'Color 3D printing · Multicolor 3D prints',
  answer:
    'PrintAWorld is a full color 3D printing service in New York for figurines, mascots, architectural models, product replicas and display pieces. Send a color 3D file, or a plain file with color references, and we review it and quote the route: a detailed resin print hand painted in your colors in our studios, or a part printed in color. Full-color jobs are quoted per project, there’s no setup fee, and we ship to all 50 states.',
  hero: 'corgi',
  faqHeading: 'What do people ask about full color 3D printing?',
  quoteText:
    'Send the file, color references or a texture, the size and the quantity. We’ll reply with the color route, a price and a schedule.',
  reviewedDate,
}

const card = {
  label: 'In color',
  title: 'Full-color 3D printing',
  body: 'Figurines, mascots and models in full color, painted or printed in color.',
}

const photos = {
  corgi: {
    url: `${photoRoot}/7EZLL7urxzTxmA4Fma5Aie/427d547551992e2b79e99873bac4444c/LAL_8396.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A rainbow-colored corgi figure on a white base beside three colorful printed pebbles',
    title: 'Rainbow corgi and color samples',
  },
  pets: {
    url: `${photoRoot}/3ZC9srB7MD0tWzJ3730Ogl/70171c7d571f56f3459788fb634c4230/LAL_8349.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A realistic brown poodle figure next to a rainbow-colored corgi figure',
    title: 'Pet figures in color',
    caption: 'A realistic brown poodle beside a rainbow corgi.',
  },
  family: {
    url: `${photoRoot}/1Hla0L7WmJiye2lczo0pqC/d52b4cc3718b86b960640cd84915a900/LAL_8791.jpg`,
    width: 2002,
    height: 3000,
    alt: 'A full-color figurine of a woman in a pink dress holding a baby',
    title: 'Figurine of a mother and baby',
    caption: 'A person figurine in full color, down to the dress pattern.',
  },
  building: {
    url: `${photoRoot}/4aeIcYVGscNg4lS2cTurJv/8fb2ac33c54cdff41ff18735a2697197/LAL_8645.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A color architectural model of a brick building with dark windows and a cornice',
    title: 'Brick building model',
    caption: 'An architectural model with brick, window and trim colors.',
  },
  sculpture: {
    url: `${photoRoot}/33eadFRtkpbMjbkgLPcLWR/e05fe6fb96f4929ee40bfdc9f739bf59/L1030812.jpg`,
    width: 3333,
    height: 5000,
    alt: 'A 6-foot painted sculpture of a running figure in a gallery',
    title: 'Painted 6-foot sculpture',
    caption: 'Printed in sections and hand painted for the artist Austin Lee.',
  },
  creature: {
    ...printing.photos.creature,
    title: 'Glossy red creature',
    caption: 'A sculpture finished in a deep gloss red.',
  },
  mascot: {
    ...printing.photos.figure,
    title: 'Brand mascot figure',
    caption: 'A shopping-bag mascot in its brand green.',
  },
}

const intro = {
  eyebrow: 'Color, not just shape',
  heading: 'What is a full color 3D printing service?',
  paragraphs: [
    'Most 3D printers print in one color at a time. A full color 3D printing service gives you a finished piece in all its colors: skin tones, logos, patterns, brick and glass, without painting it yourself.',
    [
      'There are two ways to get there. The part can be printed in color from a textured file, or printed in detailed resin and hand painted. We hand paint in our New York studios, which suits figurines, mascots, props and display pieces, and we quote printed-in-color parts per project. For figures sculpted from photos, see ',
      { to: '/services/custom-figurines/', label: 'custom figurines' },
      '.',
    ],
  ],
}

const glance = {
  heading: 'Our full color 3D printing service at a glance',
  rows: [
    ['Studios', studios],
    ['Delivery', delivery],
    [
      'Color routes',
      'Resin print hand painted in our studios, or a part printed in color, quoted per project',
    ],
    [
      'Common projects',
      'Figurines, brand mascots, pet figures, architectural models, product replicas and display props',
    ],
    [
      'Color files',
      '3MF, OBJ with its MTL and texture files (zipped), PLY, GLB or VRML (zipped). A plain STL plus color references works for painting.',
    ],
    ['Order size', 'One piece to production runs'],
    ['Price', 'Quoted per project. No setup fee.'],
    ['How you get a price', 'A written quote after a person reviews your file'],
    ['No file?', noFile],
    ['Not offered', 'Sandstone color prints'],
  ],
}

const work = {
  heading: 'Full color 3D prints from our studio.',
  photos: ['pets', 'family', 'building', 'sculpture', 'creature', 'mascot'],
}

const before = [
  {
    id: 'routes',
    eyebrow: 'Two ways to get color',
    heading:
      'Painted or printed in color: which full color 3D printing do you need?',
    index: '02 / THE COLOR',
    cards: [
      {
        label: 'C1',
        title: 'Hand painted',
        body: 'A detailed resin print, primed and painted by hand in your colors. Best for figurines, mascots, props and pieces that will be handled or seen up close. Done in our studios.',
      },
      {
        label: 'C2',
        title: 'Printed in color',
        body: 'The color comes from the file’s texture and is printed into the part. Best for photo-real textures, gradients and small runs of the same piece. Quoted per project.',
      },
      {
        label: 'C3',
        title: 'Plated or metallic',
        body: 'Chrome, gold or bronze looks from metal plating over resin, alone or with painted details. Plating coats the surface; the part isn’t solid metal.',
      },
    ],
    note: 'Not sure which? Send the file and a photo of the look you want. We’ll tell you which route gets closest and what it costs.',
  },
  {
    id: 'files',
    eyebrow: 'File prep',
    heading: 'What files do you need for color 3D printing?',
    index: '03 / THE FILE',
    paragraphs: [
      'For a part printed in color, the color has to be inside the file. Export with its textures or vertex colors included: 3MF, OBJ with its MTL and texture images, PLY, GLB or VRML. Zip a model that comes as several files, and upload it to the quote planner; files up to 500 MB each are fine.',
      'For a hand-painted piece, a plain STL, OBJ or STEP is enough. Send color references with it: a render, photos, brand colors or Pantone numbers.',
      'We check every color file by hand. If it won’t print, we tell you why, and we can rebuild it for you; the quote says what that costs.',
    ],
  },
  {
    id: 'uses',
    eyebrow: 'What it’s for',
    heading: 'What can you make with full color 3D printing?',
    index: '04 / THE USES',
    cards: [
      {
        label: 'U1',
        title: 'Figurines and mascots',
        body: [
          'People, pets and brand characters in full color. See ',
          { to: '/services/custom-figurines/', label: 'custom figurines' },
          '.',
        ],
      },
      {
        label: 'U2',
        title: 'Architectural models',
        body: [
          'Brick, stone, glass and landscape in color for presentations. See ',
          {
            to: '/services/architectural-3d-printing/',
            label: 'architectural 3D printing',
          },
          '.',
        ],
      },
      {
        label: 'U3',
        title: 'Product replicas',
        body: 'Packaging and products in brand colors for pitches, shoots and displays.',
      },
      {
        label: 'U4',
        title: 'Visual aids',
        body: 'Color-coded models that explain a part, a site or a process at a glance.',
      },
    ],
  },
]

const contrast = {
  id: 'no-file',
  eyebrow: 'No 3D file?',
  heading: 'Can you make a full color 3D print from a photo?',
  paragraphs: [
    'Yes. We model it from your photos or sketch, send the model for approval, then print and paint it. Front and side photos with the colors you want are enough to start.',
    `3D modeling starts at $${pricing.modelingFrom}. To copy a real object, 3D scanning starts at $${pricing.scanningFrom}.`,
  ],
  links: [
    { to: '/services/3d-print-from-photo/', label: '3D print from a photo' },
    { to: '/services/3d-modeling/', label: '3D modeling' },
  ],
}

const fit = {
  eyebrow: 'Is it a fit?',
  heading: 'Is our color 3D printing service right for your project?',
  index: '05 / THE FIT',
  good: [
    'Brands and agencies that need mascots, replicas or display pieces in their colors',
    'Figurines of people, pets and characters',
    'Architectural and presentation models in color',
    'Pieces that will be seen up close or handled',
    'Runs of the same painted figure',
  ],
  other: [
    'Sandstone color prints: we don’t offer them.',
    'Two- or three-color plastic prints for fun: a hobbyist with a multi-color printer will usually cost less.',
    hobby,
  ],
}

const pricingBlock = {
  heading: 'How much does full color 3D printing cost?',
  index: '06 / THE PRICE',
  lead: {
    label: 'Full-color jobs are',
    value: 'Quoted',
    note: 'per project, after we check the file and the look you want.',
  },
  rows: [
    ['3D printing from a ready file', `From about $${pricing.printFrom}`],
    ['Hand painting in full color', 'Quoted per project'],
    ['Parts printed in color', 'Quoted per project'],
    ['3D modeling, if there’s no file', `From $${pricing.modelingFrom}`],
    ['Setup fee', 'None'],
    ['Shipping, next-day available', 'Quoted to your ZIP'],
  ],
  note: 'Size, detail, number of colors, quantity and deadline set the price. Your written quote confirms it.',
}

const steps = {
  heading: 'How our color 3D printing service works',
  index: '07 / THE PROCESS',
  items: [
    ...quoteSteps,
    {
      title: 'We print it',
      body: 'Once you approve the quote and the color route, we print the piece.',
    },
    {
      title: 'Color and finish',
      body: 'We prime and paint, plate, or clean up the color print, and check it against your references.',
    },
    handoffStep,
  ],
}

const checklist = [
  'Your color 3D file (3MF, OBJ + MTL + textures, PLY, GLB or VRML), or a plain file and color references',
  'Photos, renders, brand colors or Pantone numbers',
  'Finished size',
  'How many you need',
  'What it’s for: display, handling, a shoot, a gift',
  'Your delivery ZIP code and deadline',
]

const faqs = [
  {
    question: 'How much does full color 3D printing cost?',
    answer: `It’s quoted per project, after we see the file and the look you want. Size, detail, the number of colors and the quantity set the price. 3D printing at PrintAWorld starts around $${pricing.printFrom} for a simple print from a ready file, and 3D modeling starts at $${pricing.modelingFrom} if you don’t have one. There’s no setup fee.`,
  },
  {
    question: 'Is full color 3D printing printed in color or painted?',
    answer:
      'Both exist. A part can be printed in color from a textured file, or printed in resin and painted by hand. We hand paint in our New York studios and quote printed-in-color parts per project; the quote names the route for your piece.',
  },
  {
    question: 'What file format do I need for color 3D printing?',
    answer:
      'For printed-in-color parts the file has to carry its colors: 3MF, OBJ with its MTL and texture images, PLY, GLB or VRML. Zip models that come as several files. For a painted piece, a plain STL, OBJ or STEP plus color references is enough.',
  },
  {
    question: 'Can you match brand colors?',
    answer:
      'Send Pantone numbers or a physical sample and we paint to them, then check the result against your reference before it ships. If an exact match matters, ask for a test piece in your quote.',
  },
  {
    question: 'Can you 3D print a full color figurine from a photo?',
    answer: `Yes. We sculpt the 3D model from your photos, send it for approval, then print and paint it. 3D modeling starts at $${pricing.modelingFrom}. See custom figurines for people and pets.`,
  },
  {
    question: 'Do you offer sandstone 3D printing?',
    answer:
      'No. Sandstone color prints don’t give the quality and finish we want for color work, so we print in resin and paint, or quote a printed-in-color part.',
  },
  {
    question: 'Is there a minimum order for color 3D printing?',
    answer:
      'No. One piece is fine, and so is a production run of the same painted figure. There’s no setup fee for 3D printing.',
  },
  {
    question: 'Do you ship full color 3D prints?',
    answer:
      'Yes, to all 50 states, with next-day shipping available once your piece is ready. In New York we can deliver by courier, or you can pick up by appointment in Glendale, Queens or Brooklyn.',
  },
]

module.exports = {
  page,
  card,
  photos,
  intro,
  glance,
  work,
  before,
  contrast,
  fit,
  pricing: pricingBlock,
  steps,
  checklist,
  faqs,
  schema: {
    name: 'Full Color 3D Printing Service',
    serviceType: 'Full-color 3D printing',
    description:
      'Full color 3D printing in New York for figurines, mascots, architectural models and product replicas: detailed resin prints hand painted in your colors, or parts printed in color, quoted per project and shipped to all 50 states.',
    offer:
      'Quoted per project; 3D printing from about $30 for a simple print from a ready file.',
  },
}
